/**
 * Proof Tree Component
 *
 * Implements:
 * - REQ-013: Proof Tree Visualization
 * - CON-007: Proof Tree Component Contract
 *
 * Displays defeasible logic derivation trees with the mockup's styling.
 */

import { useMemo } from "react";

interface LogEntry {
  seq: number;
  ts: string;
  agent: string;
  forms: string[];
  sig: string;
}

interface ProofTreeProps {
  task: string;
  entries: LogEntry[];
}

interface ProofData {
  literal: string;
  status: "+d" | "-d";
  rule?: string;
  ruleBody?: string;
  supports: { literal: string; status: "+D" | "+d" | "-D" | "-d"; source?: string }[];
  defeater?: {
    label: string;
    condition: string;
    note?: string;
    agent?: string;
  };
}

export default function ProofTree({ task, entries }: ProofTreeProps) {
  const proof = useMemo(() => generateProof(task, entries), [task, entries]);

  const isProven = proof.status === "+d";
  const borderColor = isProven ? "border-success/30" : "border-error/30";

  return (
    <div>
      {/* Header with status badge */}
      <div className="flex items-center gap-2 mb-2">
        <span className={`badge badge-sm ${isProven ? "badge-success" : "badge-error"}`}>
          {proof.status}
        </span>
        <span className="lt font-semibold">{proof.literal}</span>
      </div>

      {/* Proof tree */}
      <div className={`ml-4 pl-3 border-l-2 ${borderColor}`}>
        {/* Rule used */}
        {proof.rule && (
          <div className="text-base-content/60 mb-2">
            via <span className="lb">{proof.rule}</span>:{" "}
            <span className="pn">(</span>
            <span className="fm">and</span> {proof.ruleBody}
            <span className="pn">)</span> → {proof.literal}
          </div>
        )}

        {!isProven && !proof.rule && (
          <div className="text-base-content/60 mb-2">
            would prove via <span className="lb">r-{task}</span>:{" "}
            <span className="pn">(</span>
            <span className="fm">and</span> deps-{task}
            <span className="pn">)</span> → {proof.literal}
          </div>
        )}

        {/* Supporting facts */}
        <div className="ml-2 space-y-1">
          {proof.supports.map((support, i) => (
            <div key={i}>
              <span
                className={`badge badge-xs ${
                  support.status.startsWith("+") ? "badge-success" : "badge-error"
                }`}
              >
                {support.status}
              </span>{" "}
              <span className="lt">{support.literal}</span>{" "}
              {support.source && (
                <span className="text-base-content/40">({support.source})</span>
              )}
            </div>
          ))}
        </div>

        {/* Defeater section */}
        {proof.defeater && (
          <div className="mt-3 p-2 bg-warning/10 rounded">
            <div className="text-warning text-xs mb-1">
              ⚠ DEFEATED by <span className="lb">{proof.defeater.label}</span>:
            </div>
            <div>
              <span className="pn">(</span>
              <span className="kw">except</span>{" "}
              <span className="lb">{proof.defeater.label}</span>{" "}
              <span className="lt">{proof.defeater.condition}</span>{" "}
              <span className="pn">(</span>
              <span className="fm">not</span>{" "}
              <span className="lt">{proof.literal}</span>
              <span className="pn">))</span>
            </div>
            {proof.defeater.note && (
              <div className="text-base-content/50 text-xs mt-1">
                @{proof.defeater.agent || "unknown"}: "{proof.defeater.note}"
              </div>
            )}
          </div>
        )}

        {/* No defeaters message */}
        {isProven && !proof.defeater && (
          <div className="text-success mt-2">✓ no defeaters</div>
        )}

        {/* Missing deps message */}
        {!isProven && !proof.defeater && (
          <div className="text-base-content/50 mt-2">
            missing:{" "}
            {proof.supports
              .filter((s) => s.status.startsWith("-"))
              .map((s) => s.literal)
              .join(", ")}
          </div>
        )}
      </div>
    </div>
  );
}

function generateProof(task: string, entries: LogEntry[]): ProofData {
  // Extract facts from entries
  const facts = new Set<string>();
  const blocked = new Map<string, { note?: string; agent?: string }>();

  for (const entry of entries) {
    for (const form of entry.forms) {
      // Parse given facts
      const givenMatch = form.match(/\(given ([a-z0-9-]+)\)/);
      if (givenMatch) {
        facts.add(givenMatch[1]);
      }

      // Parse blocked with notes
      const blockedMatch = form.match(/\(given blocked-([a-z0-9-]+)\)/);
      if (blockedMatch) {
        // Look for note in claims
        const noteMatch = form.match(/:note "([^"]+)"/);
        blocked.set(blockedMatch[1], {
          note: noteMatch?.[1],
          agent: entry.agent,
        });
      }
    }
  }

  // Check if task is completed, blocked, or ready
  const isCompleted = facts.has(`completed-${task}`);
  const isBlocked = blocked.has(task) || facts.has(`blocked-${task}`);
  const isClaimed = facts.has(`claimed-${task}`);
  const isReady = facts.has(`ready-${task}`);
  const hasTask = facts.has(`task-${task}`);

  // Build proof based on status
  if (isCompleted) {
    return {
      literal: `ready-${task}`,
      status: "+d",
      rule: `r-done-${task}`,
      ruleBody: `task-${task}, completed-${task}`,
      supports: [
        { literal: `task-${task}`, status: "+D", source: "given" },
        { literal: `completed-${task}`, status: "+D", source: "given" },
      ],
    };
  }

  if (isBlocked) {
    const blockInfo = blocked.get(task) || {};
    return {
      literal: `ready-${task}`,
      status: "-d",
      supports: [
        { literal: `task-${task}`, status: hasTask ? "+D" : "-d", source: hasTask ? "given" : "not proven" },
        { literal: `deps-met-${task}`, status: "-d", source: "not proven" },
      ],
      defeater: {
        label: `d-block-${task}`,
        condition: `blocked-${task}`,
        note: blockInfo.note || "blocked",
        agent: blockInfo.agent,
      },
    };
  }

  if (isClaimed || isReady) {
    return {
      literal: `ready-${task}`,
      status: "+d",
      rule: `r-ready-${task}`,
      ruleBody: `task-${task}, deps-met-${task}`,
      supports: [
        { literal: `task-${task}`, status: "+D", source: "given" },
        { literal: `deps-met-${task}`, status: "+d", source: "derived" },
      ],
    };
  }

  // Default: backlog task (not ready yet)
  return {
    literal: `ready-${task}`,
    status: "-d",
    supports: [
      { literal: `task-${task}`, status: hasTask ? "+D" : "-d", source: hasTask ? "given" : "not proven" },
      { literal: `deps-met-${task}`, status: "-d", source: "not proven" },
    ],
  };
}
